let originalFetch;
let debounceTimer;

function interceptFetch() {
  if (!originalFetch) {
    originalFetch = window.fetch;
  }

  window.fetch = function (...args) {
    const [url] = args;

    return originalFetch
      .apply(this, args)
      .then(response => {
        if (typeof url === 'string' && url.includes('bc-sf-filter')) {
          if (response.ok) {
            if (url.includes('/search/suggest') || url.includes('/search?')) {
              response
                .clone()
                .json()
                .then(data => {
                  updateSearchResults(data);
                })
                .catch(console.error);
            }
          }
        }

        return response;
      })
      .catch(error => {
        if (typeof url === 'string' && url.includes('bc-sf-filter')) {
          console.error('Boost API request failed:', error);
        }

        throw error;
      });
  };
}

function buildInstantSearchApiUrl(query) {
  const baseUrl = 'https://services.mybcapps.com/bc-sf-filter/search';

  const params = new URLSearchParams({
    shop: window.location.hostname,
    q: query,
    product_limit: '16',
    collection_limit: '10',
    product_available: 'false',
    variant_available: 'false',
    page: '1',
    sort_by: 'relevance',
    filters: 'true',
    aggregations: 'true',
    include_filters: 'true',
    t: Date.now().toString(),
  });

  const fullUrl = `${baseUrl}?${params.toString()}`;

  return fullUrl;
}

async function fetchInstantSearchResults(query) {
  try {
    const apiUrl = buildInstantSearchApiUrl(query);

    const response = await fetch(apiUrl);

    if (!response.ok) {
      throw new Error(`API responded with status: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('❌ DEBUG: Instant Search API Error:', error);
    console.error('Instant Search API Error:', error);
    throw error;
  }
}

function getMetafieldValue(product, namespace, key) {
  if (!product.metafields || !Array.isArray(product.metafields)) {
    return null;
  }

  const metafield = product.metafields.find(mf => mf.namespace === namespace && mf.key === key);

  return metafield ? metafield.value : null;
}

function reinitializeProductCards(container) {
  // Find all product-card elements in the container
  const productCards = container.querySelectorAll('product-card');

  productCards.forEach(productCard => {
    // Force the existing product-card component to reinitialize
    if (productCard.setupQuickShopButtons && typeof productCard.setupQuickShopButtons === 'function') {
      productCard.setupQuickShopButtons();
    }

    // Also ensure makeCardClickable is set up if the method exists
    if (productCard.makeCardClickable && typeof productCard.makeCardClickable === 'function') {
      productCard.makeCardClickable();
    }

    // Check Quick Shop buttons status (but don't add conflicting listeners)
    const quickShopButtons = productCard.querySelectorAll('.jsQuickShop');

    quickShopButtons.forEach(button => {
      if (!button.hasAttribute('data-quickshop-listener')) {
        console.warn('⚠️ Found Quick Shop button without listener, component may need manual setup');
      }

      if (!button.hasAttribute('data-quickshop-url')) {
        console.warn('⚠️ Found Quick Shop button without data-quickshop-url attribute');
      }

      button.addEventListener(
        'click',
        function debugQuickShopHandler() {
          // Try to manually call the method if it exists
          if (productCard.onFetchQuickShop && typeof productCard.onFetchQuickShop === 'function') {
            try {
              productCard.onFetchQuickShop(button);
            } catch (error) {
              console.error('❌ Error calling onFetchQuickShop:', error);
            }
          }
        },
        { once: false, capture: true }
      );
    });
  });
}

function renderProducts(products) {
  const productListContainer = document.querySelector('.boost-sd__product-list');

  if (!productListContainer) {
    console.warn('❌ Product list container (.boost-sd__product-list) not found');

    return;
  }

  if (!products || products.length === 0) {
    productListContainer.innerHTML = '<div class="no-results">No products found</div>';

    return;
  }

  // Add missing CSS class to match template
  if (!productListContainer.classList.contains('boost-sd__product-list-undefined-col')) {
    productListContainer.classList.add('boost-sd__product-list-undefined-col');
  }

  // Get current search query for highlighting
  const currentSearchQuery = getCurrentSearchQuery();

  // Add CSS styles for highlighting
  addSearchHighlightStyles();

  const productsHtml = products
    .map(product => {
      const mainImage = product.images_info?.[0]?.src || product.images?.['1'] || '';
      const productUrl = `/products/${product.handle}`;

      // Enhanced vendor/author processing to match Liquid template
      // Liquid template uses: author_full_names = product | product_metafield: 'asp.author_full_names'
      let authorFullNames = product.author_full_names || getMetafieldValue(product, 'asp', 'author_full_names');

      // Clean vendor name - remove " ( Development )" suffix
      let vendor = product.author || product.vendor || '';

      vendor = vendor.replace(/\s*\(\s*Development\s*\)/i, '').trim();

      const title = product.title || '';
      const highlightedTitle = highlightSearchKeywords(title, currentSearchQuery);

      // Highlight author names as well
      const highlightedAuthorFullNames = highlightSearchKeywords(authorFullNames || '', currentSearchQuery);
      const highlightedVendor = highlightSearchKeywords(vendor, currentSearchQuery);

      // Get variants info first (needed for price analysis)
      const variants = product.variants || [];
      const firstVariant = variants[0] || {};

      const variantsMetafield = getMetafieldValue(product, 'algolia', 'variants');
      let viewableVariants = [];

      if (variantsMetafield) {
        try {
          const variantsData = JSON.parse(variantsMetafield);

          if (variantsData.variants && Array.isArray(variantsData.variants)) {
            // Use metafield variants data which includes viewableOnEstore
            viewableVariants = variantsData.variants.filter(v => v.viewableOnEstore === true);
          }
        } catch (parseError) {
          console.warn('Could not parse variants metafield:', parseError);
        }
      }

      // Track if we're using fallback variants (no viewableOnEstore=true variants found)
      let usingFallback = false;

      // Fallback to API variants if no metafield data
      if (viewableVariants.length === 0) {
        usingFallback = true;
        viewableVariants = variants.filter(v => v.available !== false);
      }

      // Determine price display based on fallback status
      let priceDisplay;

      if (usingFallback) {
        priceDisplay = 'Viewable on Estore';
      } else {
        // Calculate price from filtered viewable variants instead of product.price_min/max
        const variantPrices = viewableVariants.map(v => parseFloat(v.price)).filter(p => !isNaN(p));
        const minPrice = Math.min(...variantPrices);
        const maxPrice = Math.max(...variantPrices);

        const priceFrom = formatPrice(minPrice, maxPrice);
        priceDisplay = `from ${priceFrom}`;
      }

      const variantId = firstVariant.id || '';
      const productId = product.id || '';

      // Create variant list for display (show up to 2, then "more") - matching Liquid template
      let displayedVariants = 0;

      const variantDisplay = viewableVariants
        .slice(0, 2)
        .map((variant, index) => {
          displayedVariants++;

          const variantTitle = variant.title || `Variant ${index + 1}`;
          const variantBarcode = variant.barcode || variant.sku || '';

          // Create variant handle for icon mapping (matching Liquid logic)
          const variantHandle = variantTitle
            .toLowerCase()
            .replace(/ /g, '_')
            .replace(/-/g, '_')
            .replace(/ebook/g, 'e_book');

          return `
          <div class="variant-item flex">
            <div class="variant-icons">
              <!-- Icon mapping would go here based on variant_handle: ${variantHandle} -->
            </div>
            <div class="variant-item-info">
              <div class="variant-title">${variantTitle}</div>
              <div class="variant-barcode">${variantBarcode}</div>
            </div>
          </div>
        `;
        })
        .join('');

      // Calculate remaining variants (matching Liquid template logic)
      const remainingVariants = viewableVariants.length - displayedVariants;

      const moreVariantsHtml =
        remainingVariants > 0
          ? `<div class="varriant-more ${variants.length}">
          <span>+ ${remainingVariants} more ${remainingVariants > 1 ? 'options' : 'option'}</span>
        </div>`
          : '';

      // Get tags for hidden info
      const tags = (product.tags || [])
        .map(tag => `<span class="ais-hit--tag">${tag}</span>`)
        .join('\n                  ');

      return `
        <product-card class="ais-hit ais-product boost-product-card" data-handle="${product.handle}" data-variant-id="${variantId}" data-product-id="${productId}" style="cursor: pointer;">
          <div class="ais-hit--picture" style="background-image: url('${mainImage}')"></div>
          <div class="ais-hit--details">
            <div class="product-card--title-price">
              <p class="ais-hit--title">
                <a href="${productUrl}?variant=${variantId}" title="${title}">
                  ${highlightedTitle}
                </a>
              </p>
              <p class="ais-hit--price"></p>
              <div>
                ${priceDisplay}
              </div>
              <p></p>
            </div>

            <p class="ais-hit--subtitle">${highlightedAuthorFullNames || highlightedVendor}</p>

            <div class="product-variant none md-block">
              <div class="variant-item-wrapper">
                ${variantDisplay}
              </div>
              ${moreVariantsHtml}
            </div>

            <p class="ais-hit--info" style="display: none">
              <span class="algolia-sku">${firstVariant.sku || ''}</span>
              <span class="algolia-barcode">${firstVariant.barcode || firstVariant.sku || ''}</span>
            </p>

            <p class="ais-hit--tags" style="display: none">
              ${tags}
            </p>

            <div class="product-card--variant-title button-search">
              <button class="c-pointer jsQuickShop btn btn-1" data-quickshop-url="${productUrl}?variant=${variantId}&section_id=popup-instock" data-quickshop-listener="true">
                Quick Shop
              </button>
            </div>
          </div>

          <div class="product-variant product-variant-moblie none md-block">
            <div class="variant-item-wrapper">
              ${variantDisplay}
            </div>
            ${moreVariantsHtml}
          </div>

          <div class="content-description-card-footer">
            <p class="ais-hit--price"></p>
            <div>
              ${priceDisplay}
            </div>
            <p></p>

            <div class="buttomn-hidden-info card-control">
              <div class="show-detail flex control-toggle">
                <div class="svg_wrapper"></div>
                <span>Show Details</span>
              </div>
              <div class="hide-detail flex control-toggle">
                <div class="svg_wrapper"></div>
                <span>Hide Details</span>
              </div>
            </div>
          </div>
        </product-card>
      `;
    })
    .join('');

  productListContainer.innerHTML = productsHtml;

  // Manually trigger product-card component initialization for dynamically added elements
  reinitializeProductCards(productListContainer);
}

function renderFilters(filterData) {
  const filterContainer = document.querySelector('.boost-sd__filter-wrapper');

  if (!filterContainer) {
    return;
  }

  if (!filterData || !filterData.options || filterData.options.length === 0) {
    return;
  }

  let filtersHTML = '';

  filterData.options.forEach(option => {
    if (option.values && option.values.length > 0) {
      filtersHTML += `
        <div class="filter-group">
          <h3>${option.label}</h3>
          <ul>
            ${option.values
              .map(
                value => `
              <li>
                <label>
                  <input type="checkbox" value="${value.value}" />
                  ${value.label} (${value.count})
                </label>
              </li>
            `
              )
              .join('')}
          </ul>
        </div>
      `;
    }
  });

  filterContainer.innerHTML = filtersHTML;
}

function formatPrice(minPrice) {
  try {
    const min = parseFloat(minPrice) || 0;

    // If min price is 0, that means no valid price data
    if (min === 0) {
      return 'Price not available';
    }

    // Convert from dollars to cents for formatMoney function
    const minInCents = Math.round(min * 100);

    if (window.currencies_default?.formatMoney) {
      try {
        // Use money format from API meta or fallback
        const formatString = window.boostApiMoneyFormat || window.theme?.moneyFormat || '${{amount}}';
        const formatted = window.currencies_default.formatMoney(minInCents, formatString);

        return formatted;
      } catch (formatError) {
        console.error('formatMoney error:', formatError);
        // Fall back to manual formatting
      }
    }

    // Manual formatting fallback
    const symbol = window.theme?.settings?.currency_symbol || '$';
    const formattedPrice = `${symbol}${min.toFixed(2)}`;

    return formattedPrice;
  } catch (error) {
    console.error('Error formatting price:', error);

    return '$0.00';
  }
}

function handleMetafieldFilter(filterType, value, isChecked) {
  if (!window.activeMetafieldFilters) {
    window.activeMetafieldFilters = {};
  }

  if (!window.activeMetafieldFilters[filterType]) {
    window.activeMetafieldFilters[filterType] = new Set();
  }

  if (isChecked) {
    window.activeMetafieldFilters[filterType].add(value);
  } else {
    window.activeMetafieldFilters[filterType].delete(value);
  }

  applyMetafieldFilters();

  // Update browser URL with current filters
  updateBrowserUrl();

  // Immediately update the selected filters section
  updateSelectedFiltersSection();
}

function applyMetafieldFilters() {
  if (!window.lastSearchData || !window.lastSearchData.products) {
    return;
  }

  const allProducts = window.lastSearchData.products;

  // Filter products based on active filters
  const filteredProducts = allProducts.filter(product => shouldShowProduct(product));

  // Re-render the filtered products
  renderProducts(filteredProducts);

  // Update product count
  updateProductCount(filteredProducts.length);

  // Update clear buttons visibility
  updateClearButtonsVisibility();

  // Update pagination based on filtered results
  updatePagination(filteredProducts.length);

  // Update selected filters section
  updateSelectedFiltersSection();

  // Update search form title to reflect current search state
  updateSearchFormTitle();
}

function shouldShowProduct(product) {
  if (!window.activeMetafieldFilters || Object.keys(window.activeMetafieldFilters).length === 0) {
    return true;
  }

  return Object.entries(window.activeMetafieldFilters).every(([filterType, selectedValues]) => {
    if (selectedValues.size === 0) {
      return true;
    }

    if (!product.metafields) {
      return false;
    }

    // Extract the key from the full namespace format (e.g., "::asp::catalog" -> "catalog")
    const metafieldKey = filterType.replace(/^::asp::/, '');
    const aspMetafield = product.metafields.find(m => m.namespace === 'asp' && m.key === metafieldKey);

    if (!aspMetafield) {
      return false;
    }

    try {
      const values = JSON.parse(aspMetafield.value);

      if (Array.isArray(values)) {
        return values.some(value => selectedValues.has(value));
      } else {
        return selectedValues.has(values);
      }
    } catch {
      return selectedValues.has(aspMetafield.value);
    }
  });
}

function updateClearButtonsVisibility() {
  if (!window.activeMetafieldFilters) {
    return;
  }

  // Check each filter type and show/hide clear buttons
  Object.entries(window.activeMetafieldFilters).forEach(([filterType, selectedValues]) => {
    // For mobile filters: pf_m___asp__catalog-clear (with underscores)
    const mobileSelectorId = CSS.escape(`pf_m___asp__${filterType.replace(/^::asp::/, '')}-clear`);
    const mobileClearButton = document.querySelector(`#${mobileSelectorId}`);

    // For desktop filters: pf_m_::asp::catalog-clear (with colons)
    const desktopSelectorId = CSS.escape(`pf_m_${filterType}-clear`);
    const desktopClearButton = document.querySelector(`#${desktopSelectorId}`);

    // Update mobile clear button
    if (mobileClearButton) {
      if (selectedValues.size > 0) {
        mobileClearButton.classList.remove('boost-sd__g-hide');
      } else {
        mobileClearButton.classList.add('boost-sd__g-hide');
      }
    }

    // Update desktop clear button
    if (desktopClearButton) {
      if (selectedValues.size > 0) {
        desktopClearButton.classList.remove('boost-sd__g-hide');
      } else {
        desktopClearButton.classList.add('boost-sd__g-hide');
      }
    }
  });
}

function updateBrowserUrl() {
  const currentUrl = new URL(window.location.href);

  // Clear existing metafield filter parameters
  Array.from(currentUrl.searchParams.keys()).forEach(key => {
    if (key.startsWith('pf_m_')) {
      currentUrl.searchParams.delete(key);
    }
  });

  // Add current metafield filters to URL
  if (window.activeMetafieldFilters) {
    Object.entries(window.activeMetafieldFilters).forEach(([filterType, selectedValues]) => {
      if (selectedValues.size > 0) {
        // Let URLSearchParams handle the encoding automatically
        // filterType is already in format "::asp::catalog"
        const paramKey = `pf_m_${filterType}`;

        selectedValues.forEach(value => {
          currentUrl.searchParams.append(paramKey, value);
        });
      }
    });
  }

  // Update browser URL without page reload
  window.history.pushState({ filters: window.activeMetafieldFilters }, '', currentUrl.toString());
}

function initializeFiltersFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);

  window.activeMetafieldFilters = {};

  urlParams.forEach((value, key) => {
    if (key.startsWith('pf_m_')) {
      // URLSearchParams automatically decodes the key, so we just remove the prefix
      // key will be "pf_m_::asp::catalog" (already decoded from "pf_m_%3A%3Aasp%3A%3Acatalog")
      const filterType = key.replace('pf_m_', '');

      if (!window.activeMetafieldFilters[filterType]) {
        window.activeMetafieldFilters[filterType] = new Set();
      }

      window.activeMetafieldFilters[filterType].add(value);

      // Find and mark the corresponding filter button as selected
      const filterButton = document.querySelector(
        `[data-metafield-filter="${filterType}"][data-metafield-value="${value}"]`
      );

      if (filterButton) {
        filterButton.setAttribute('aria-checked', 'true');
        filterButton.classList.add('boost-sd__filter-option-item-button--selected');
      }
    }
  });

  if (Object.keys(window.activeMetafieldFilters).length > 0) {
    // Apply the filters and update UI
    applyMetafieldFilters();
    updateSelectedFiltersSection();
  }
}

function updateProductCount(count) {
  const productCountElement = document.querySelector('.boost-sd__product-count');
  const toolbarProductCountElement = document.querySelector('.boost-sd__toolbar-item--product-count .result-counting');

  if (productCountElement) {
    const productsPerPage = 16;
    const startEntry = count > 0 ? 1 : 0;
    const endEntry = Math.min(productsPerPage, count);

    productCountElement.innerHTML = `
      Showing <span>${startEntry}</span> to <span>${endEntry}</span> of <span>${count}</span> entries
    `;
  }

  if (toolbarProductCountElement) {
    const productsPerPage = 16;
    const startEntry = count > 0 ? 1 : 0;
    const endEntry = Math.min(productsPerPage, count);

    toolbarProductCountElement.innerHTML = `
      Showing <span>${startEntry}</span> to <span>${endEntry}</span> of <span>${count}</span> entries
    `;
  }

  const showResultButton = document.querySelector(
    '.boost-sd__filter-tree-vertical-show-result .boost-sd__button--result'
  );

  if (showResultButton) {
    const buttonText = showResultButton.querySelector('.boost-sd__button-text');

    if (buttonText) {
      buttonText.textContent = `Show result (${count})`;
    }
  }
}

function updatePagination(totalProducts, productsPerPage = 16) {
  let paginationContainer = document.querySelector('.boost-sd__pagination');

  // If pagination container doesn't exist, create it
  if (!paginationContainer) {
    const productListContainer = document.querySelector('.boost-sd__product-list');

    if (productListContainer && productListContainer.parentNode) {
      paginationContainer = document.createElement('div');
      paginationContainer.className = 'boost-sd__pagination boost-sd__pagination--center';

      // Insert the pagination container after the product list
      productListContainer.parentNode.insertBefore(paginationContainer, productListContainer.nextSibling);
    } else {
      return;
    }
  }

  const totalPages = Math.ceil(totalProducts / productsPerPage);
  const currentPage = 1;

  if (totalPages <= 1) {
    paginationContainer.style.display = 'none';

    return;
  }

  // Restore flex display for pagination
  paginationContainer.style.display = 'flex';

  let paginationHTML = '';

  if (currentPage > 1) {
    paginationHTML += `
      <button type="button" aria-label="previous page" class="boost-sd__pagination-button boost-sd__pagination-button--square boost-sd__pagination-button--prev">
        <span class="boost-sd__pagination-button-icon boost-sd__pagination-button-icon--left">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 7 12" height="12" width="7">
            <path d="M6.80474 11.4715C6.54439 11.7318 6.12228 11.7318 5.86193 11.4715L0.861928 6.47149C0.601578 6.21114 0.601578 5.78903 0.861928 5.52868L5.86193 0.528679C6.12228 0.268329 6.54439 0.268329 6.80474 0.528679C7.06509 0.789029 7.06509 1.21114 6.80474 1.47149L2.27614 6.00008L6.80474 10.5287C7.06509 10.789 7.06509 11.2111 6.80474 11.4715Z" clip-rule="evenodd" fill-rule="evenodd"></path>
          </svg>
        </span>
      </button>`;
  }

  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - 2);
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  if (startPage > 1) {
    paginationHTML += `<button aria-label="Go to Page 1" class="boost-sd__pagination-number boost-sd__pagination-number--square" data-page="1">1</button>`;

    if (startPage > 2) {
      paginationHTML += `<button aria-hidden="true" class="boost-sd__pagination-number boost-sd__pagination-number--disabled boost-sd__pagination-number--square">...</button>`;
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    const isActive = i === currentPage;
    const activeClass = isActive ? ' boost-sd__pagination-number--active' : '';
    const ariaLabel = isActive ? `Current Page ${i}` : `Go to Page ${i}`;

    paginationHTML += `<button aria-label="${ariaLabel}" class="boost-sd__pagination-number boost-sd__pagination-number--square${activeClass}" data-page="${i}">${i}</button>`;
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      paginationHTML += `<button aria-hidden="true" class="boost-sd__pagination-number boost-sd__pagination-number--disabled boost-sd__pagination-number--square">...</button>`;
    }

    paginationHTML += `<button aria-label="Go to Page ${totalPages}" class="boost-sd__pagination-number boost-sd__pagination-number--square" data-page="${totalPages}">${totalPages}</button>`;
  }

  if (currentPage < totalPages) {
    paginationHTML += `
      <button type="button" aria-label="next page" class="boost-sd__pagination-button boost-sd__pagination-button--square boost-sd__pagination-button--next">
        <span class="boost-sd__pagination-button-icon boost-sd__pagination-button-icon--right">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 7 12" height="12" width="7">
            <path d="M0.195262 0.528514C0.455612 0.268165 0.877722 0.268165 1.13807 0.528514L6.13807 5.52851C6.39842 5.78886 6.39842 6.21097 6.13807 6.47132L1.13807 11.4713C0.877722 11.7317 0.455612 11.7317 0.195262 11.4713C-0.0650874 11.211 -0.0650874 10.7889 0.195262 10.5285L4.72386 5.99992L0.195262 1.47132C-0.0650874 1.21097 -0.0650874 0.788864 0.195262 0.528514Z" clip-rule="evenodd" fill-rule="evenodd"></path>
          </svg>
        </span>
      </button>`;
  }

  paginationContainer.innerHTML = paginationHTML;
}

function renderMetafieldFiltersWithBoostStructure(metafieldFilters) {
  const filterTreeWrapper = document.querySelector('#boost-sd__filter-tree-wrapper');
  const mobileFilterContainer = document.querySelector('#boost-sd_filter-tree-mobile');

  if (!filterTreeWrapper && !mobileFilterContainer) {
    return;
  }

  const metafieldMapping = {
    catalog: 'CATALOG',
    course_area: 'COURSE AREA',
    series: 'SERIES',
  };

  // Generate all metafield filter options
  const metafieldFilterOptions = Object.entries(metafieldFilters)
    .filter(([, values]) => values.size > 0)
    .map(([metafieldKey, values]) => {
      const displayName = metafieldMapping[metafieldKey] || metafieldKey;
      const sortedValues = Array.from(values).sort();

      return createBoostFilterOption(metafieldKey, displayName, sortedValues);
    })
    .join('');

  // Update desktop filter tree
  if (filterTreeWrapper) {
    const newFilterTreeContent = `
      <div class="boost-sd__filter-tree-vertical">
        <div class="boost-sd__filter-tree-vertical-content">
          <div id="bc-refine-by"></div>
          <div class="boost-sd__filter-tree-vertical-option-list">
            ${metafieldFilterOptions}
          </div>
        </div>
      </div>
      <button type="button" aria-label="Back to top" class="boost-sd__button boost-sd__scroll-to-top boost-sd__scroll-to-top--black" style="display: none;"></button>
    `;

    filterTreeWrapper.innerHTML = newFilterTreeContent;
  }

  // Update mobile filter tree
  if (mobileFilterContainer) {
    updateMobileFilterContainer(mobileFilterContainer, metafieldFilters);
  }

  setupMetafieldFilterEventListeners();

  // Hide empty filter options after rendering
  hideEmptyFilterOptions();
}

function updateMobileFilterContainer(mobileFilterContainer, metafieldFilters) {
  const metafieldMapping = {
    catalog: 'CATALOG',
    course_area: 'COURSE AREA',
    series: 'SERIES',
  };

  const mobileMetafieldFilterOptions = Object.entries(metafieldFilters)
    .filter(([, values]) => values.size > 0)
    .map(([metafieldKey, values]) => {
      const displayName = metafieldMapping[metafieldKey] || metafieldKey;
      const sortedValues = Array.from(values).sort();

      return createMobileBoostFilterOption(metafieldKey, displayName, sortedValues);
    })
    .join('');

  const existingOptionList = mobileFilterContainer.querySelector('.boost-sd__filter-tree-vertical-option-list');

  if (existingOptionList) {
    existingOptionList.innerHTML = mobileMetafieldFilterOptions;
  }

  const existingShowResultButton = mobileFilterContainer.querySelector(
    '.boost-sd__filter-tree-vertical-show-result .boost-sd__button-text'
  );

  if (existingShowResultButton) {
    const newText = `Show result (${getTotalProductCount()})`;
    existingShowResultButton.textContent = newText;
  }
}

function createMobileBoostFilterOption(filterType, displayName, values) {
  const fullFilterType = `::asp::${filterType}`;
  // Use the EXACT format from the good structure: "pf_m_::asp::catalog" (with colons)
  const mobileFilterOptionId = `pf_m_::asp::${filterType}`;
  // Use underscore format for the individual element IDs
  const mobileToggleId = `pf_m___asp__${filterType}`;
  const mobileListId = `pf_m___asp__${filterType}`;

  const selectedValues = window.activeMetafieldFilters?.[fullFilterType] || new Set();
  const selectedValuesArray = Array.from(selectedValues);
  const mobileSelectedText = selectedValuesArray.length > 0 ? selectedValuesArray.join(', ') : '';

  const filterValuesHtml = values
    .map(value => {
      const itemId = `w-${Math.random().toString(36).substr(2, 20)}`;
      // Use colon format for the action key to match the good structure
      const actionKey = `pf_m_::asp::${filterType}.${itemId}`;
      const isSelected = selectedValues.has(value);

      return `
        <li class="boost-sd__filter-option-item boost-sd__filter-option-list-option-item">
          <button role="checkbox"
                  aria-checked="${isSelected}"
                  aria-label="Find product with ${displayName} is ${value}"
                  id="${itemId}"
                  class="boost-sd__filter-option-item-button boost-sd__filter-option-item-button--as-button boost-sd__filter-option-item-button--with-checkbox${isSelected ? ' boost-sd__filter-option-item-button--selected' : ''}"
                  data-action="${actionKey}"
                  data-metafield-filter="${fullFilterType}"
                  data-metafield-value="${value}">
            <span data-action="${actionKey}" class="boost-sd__filter-option-item-label">
              ${value}
            </span>
            <span data-action="${actionKey}" aria-label="Number of products: (1)" class="boost-sd__filter-option-item-amount">
              (1)
            </span>
          </button>
        </li>`;
    })
    .join('');

  const mobileFilterOptionHtml = `
    <div class="boost-sd__filter-option boost-sd__filter-option-list" data-filteroptionid="${mobileFilterOptionId}">
      <div id="${mobileToggleId}-toggle-target-label" class="boost-sd__filter-option-label">
        <button aria-expanded="true"
                aria-label="Filter by: ${displayName}"
                id="${mobileToggleId}-toggle"
                class="boost-sd__filter-option-title"
                data-mobile-filter="true"
                data-metadata="{&quot;action&quot;:{&quot;filterOptionCollapse&quot;:{&quot;actionId&quot;:&quot;${mobileToggleId}-toggle&quot;}}}">
          ${displayName}
          <svg class="boost-sd__filter-option-icon"
               xmlns="http://www.w3.org/2000/svg"
               width="7"
               height="12"
               role="img"
               aria-label="Filter by: ${displayName}"
               viewBox="0 0 7 12"
               fill="#222"
               data-metadata="{&quot;action&quot;:{&quot;filterOptionCollapse&quot;:{&quot;actionId&quot;:&quot;${mobileToggleId}-toggle&quot;}}}">
            <path class="boost-sd__filter-option-path"
                  d="M6.58942 0.610713C6.26398 0.285274 5.73635 0.285274 5.41091 0.610713L0.611415 5.41021C0.455135 5.56649 0.366829 5.77895 0.366829 5.99996C0.366829 6.22098 0.454629 6.43294 0.610909 6.58922L5.41091 11.3892C5.73635 11.7147 6.26398 11.7147 6.58942 11.3892C6.91486 11.0638 6.91486 10.5362 6.58942 10.2107L2.37867 5.99996L6.58942 1.78922C6.91486 1.46379 6.91486 0.936153 6.58942 0.610713Z"
                  data-metadata="{&quot;action&quot;:{&quot;filterOptionCollapse&quot;:{&quot;actionId&quot;:&quot;${mobileToggleId}-toggle&quot;}}}"></path>
          </svg>
        </button>
        <div class="boost-sd__filter-option-mobile-items-selected">${mobileSelectedText}</div>
        <button data-metadata="{&quot;action&quot;:{&quot;resetFilterOption&quot;:{&quot;key&quot;:&quot;${mobileFilterOptionId}&quot;}}}"
                aria-label="Clear: ${displayName}"
                id="${mobileToggleId}-clear"
                class="boost-sd__button boost-sd__button--clear boost-sd__g-hide boost-sd__filter-option-label-btn">
          <span data-metadata="{&quot;action&quot;:{&quot;resetFilterOption&quot;:{&quot;key&quot;:&quot;${mobileFilterOptionId}&quot;}}}">Clear</span>
        </button>
      </div>
      <div id="${mobileToggleId}-toggle-target" class="boost-sd__filter-option-itemwrapper boost-sd__filter-option-itemwrapper--hidden">
        <div class="boost-sd__filter-option-content-inner boost-sd__filter-option-content-inner--scrollbar">
          <ul id="${mobileListId}" class="boost-sd__filter-option-listitem">
            ${filterValuesHtml}
          </ul>
        </div>
        <button class="boost-sd__filter-option-viewmorebtn" style="display: none">View More</button>
      </div>
    </div>`;

  return mobileFilterOptionHtml;
}

function getTotalProductCount() {
  if (window.lastSearchData?.total_product !== undefined) {
    return window.lastSearchData.total_product;
  }

  if (window.lastSearchData?.products) {
    return window.lastSearchData.products.length;
  }

  return 0;
}

function createBoostFilterOption(filterType, displayName, values) {
  // Use the full metafield namespace format for data attributes and IDs
  const fullFilterType = `::asp::${filterType}`;
  const filterOptionId = `pf_m_::asp::${filterType}`;
  const filterListId = `pf_m_::asp::${filterType}`;

  // Create the filter option values HTML
  const filterValuesHtml = values
    .map(value => {
      const itemId = `w-${Math.random().toString(36).substr(2, 20)}`;
      const actionKey = `${filterListId}.${itemId}`;

      return `
        <li class="boost-sd__filter-option-item boost-sd__filter-option-list-option-item">
          <button role="checkbox"
                  aria-checked="false"
                  aria-label="Find product with ${displayName} is ${value}"
                  id="${itemId}"
                  class="boost-sd__filter-option-item-button boost-sd__filter-option-item-button--as-button boost-sd__filter-option-item-button--with-checkbox"
                  data-action="${actionKey}"
                  data-metafield-filter="${fullFilterType}"
                  data-metafield-value="${value}">
            <span data-action="${actionKey}" class="boost-sd__filter-option-item-label">
              ${value}
            </span>
            <span data-action="${actionKey}" aria-label="Number of products: (1)" class="boost-sd__filter-option-item-amount">
              (1)
            </span>
          </button>
        </li>`;
    })
    .join('');

  // Create the complete filter option structure matching Boost AI template exactly
  return `
    <div class="boost-sd__filter-option boost-sd__filter-option-list" data-filteroptionid="${filterOptionId}">
      <div id="${filterListId}-toggle-target-label" class="boost-sd__filter-option-label">
        <button aria-expanded="true"
                aria-label="Filter by: ${displayName}"
                id="${filterListId}-toggle"
                class="boost-sd__filter-option-title">
          ${displayName}
          <svg class="boost-sd__filter-option-icon"
               xmlns="http://www.w3.org/2000/svg"
               width="7"
               height="12"
               role="img"
               aria-label="Filter by: ${displayName}"
               viewBox="0 0 7 12"
               fill="#222">
            <path class="boost-sd__filter-option-path"
                  d="M6.58942 0.610713C6.26398 0.285274 5.73635 0.285274 5.41091 0.610713L0.611415 5.41021C0.455135 5.56649 0.366829 5.77895 0.366829 5.99996C0.366829 6.22098 0.454629 6.43294 0.610909 6.58922L5.41091 11.3892C5.73635 11.7147 6.26398 11.7147 6.58942 11.3892C6.91486 11.0638 6.91486 10.5362 6.58942 10.2107L2.37867 5.99996L6.58942 1.78922C6.91486 1.46379 6.91486 0.936153 6.58942 0.610713Z"></path>
          </svg>
        </button>
        <button aria-label="Clear: ${displayName}"
                id="${filterListId}-clear"
                class="boost-sd__button boost-sd__button--clear boost-sd__g-hide boost-sd__filter-option-label-btn">
          <span>Clear</span>
        </button>
      </div>
      <div id="${filterListId}-toggle-target" class="boost-sd__filter-option-itemwrapper">
        <div class="boost-sd__filter-option-content-inner boost-sd__filter-option-content-inner--scrollbar">
          <ul id="${filterListId}" class="boost-sd__filter-option-listitem">
            ${filterValuesHtml}
          </ul>
        </div>
        <button class="boost-sd__filter-option-viewmorebtn" style="display: none">View More</button>
      </div>
    </div>`;
}

function hideEmptyFilterOptions() {
  // Find all filter option items and hide those with empty values
  const filterItems = document.querySelectorAll('.boost-sd__filter-option-item');

  filterItems.forEach(item => {
    const labelSpan = item.querySelector('.boost-sd__filter-option-item-label');

    if (labelSpan) {
      const labelText = labelSpan.textContent.trim();

      // Check if the label contains only empty brackets or is effectively empty
      if (labelText === '[]' || labelText === '' || labelText === '[ ]') {
        // Hide the entire list item
        item.style.display = 'none';
      }
    }
  });
}

function highlightSearchKeywords(text, searchQuery) {
  if (!text || !searchQuery || searchQuery.trim() === '' || searchQuery === '*') {
    return text;
  }

  // CRITICAL: Check for any existing highlighting or markup before processing
  const hasExistingHighlights =
    text.includes('search-highlight') ||
    text.includes('<mark') ||
    text.includes('</mark>') ||
    text.includes('ark class=') ||
    text.includes('ark>');

  if (hasExistingHighlights) {
    return text;
  }

  // Split search query into individual keywords and clean them
  const keywords = searchQuery
    .toLowerCase()
    .split(/\s+/)
    .filter(keyword => keyword.length > 0)
    .map(keyword => keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

  if (keywords.length === 0) {
    return text;
  }

  // Create a single comprehensive regex to avoid recursive highlighting
  // Sort keywords by length (longest first) to prioritize longer matches
  const sortedKeywords = keywords.sort((a, b) => b.length - a.length);

  // Create regex that matches any of the keywords as whole or partial matches
  const combinedRegex = new RegExp(`(${sortedKeywords.join('|')})`, 'gi');

  // Single pass replacement to prevent recursive highlighting
  const highlightedText = text.replace(combinedRegex, '<mark class="search-highlight">$1</mark>');

  return highlightedText;
}

function getCurrentSearchQuery() {
  const urlParams = new URLSearchParams(window.location.search);
  const urlQuery = urlParams.get('q');

  if (urlQuery && urlQuery.trim() && urlQuery !== '*') {
    return urlQuery.trim();
  }

  // Fallback to search input value
  const searchInput = document.querySelector('.boost-sd__search-form-input, input[name="q"]');

  if (searchInput && searchInput.value.trim() && searchInput.value !== '*') {
    return searchInput.value.trim();
  }

  return '';
}

// Add CSS styles for search highlighting
function addSearchHighlightStyles() {
  // Styles already added
  if (document.getElementById('search-highlight-styles')) {
    return;
  }

  const style = document.createElement('style');

  style.id = 'search-highlight-styles';

  style.textContent = `
    .search-highlight {
      background-color: #0000001A;
      color: #333;
      font-weight: bold;
      padding: 1px 2px;
      border-radius: 2px;
    }
  `;

  document.head.appendChild(style);
}

function setupSortingEventListeners() {
  const sortingButtons = document.querySelectorAll('.boost-sd__sorting-option-item');

  sortingButtons.forEach(button => {
    button.removeEventListener('click', handleSortingClick);
    button.addEventListener('click', handleSortingClick);
  });
}

function handleSortingClick() {
  setTimeout(() => {
    highlightExistingProducts();
  }, 300);

  setTimeout(() => {
    highlightExistingProducts();
  }, 600);

  setTimeout(() => {
    highlightExistingProducts();
  }, 1000);
}

function setupFilterReorderingListeners() {
  document.addEventListener('click', event => {
    const filterButton = event.target.closest('.boost-sd__filter-option-item-button');

    if (filterButton) {
      setTimeout(() => {
        reorderSelectedFilters();

        setTimeout(() => {
          highlightExistingProducts();
        }, 100);
      }, 100);
    }
  });
}

function reorderSelectedFilters() {
  const filterLists = document.querySelectorAll('.boost-sd__filter-option-listitem');

  if (filterLists.length === 0) {
    console.warn('⚠️ No filter lists found! boost-sd__filter-tree-vertical might not be loaded yet');

    return;
  }

  filterLists.forEach((filterList, listIndex) => {
    if (filterList.children.length === 0) {
      return;
    }

    const selectedItems = [];
    const unselectedItems = [];

    const filterItems = Array.from(filterList.children);

    filterItems.forEach(item => {
      const button = item.querySelector('.boost-sd__filter-option-item-button');

      if (button) {
        const isSelected = button.classList.contains('boost-sd__filter-option-item-button--selected');

        const label = item.querySelector('.boost-sd__filter-option-item-label');

        if (isSelected) {
          if (label) {
            label.style.fontWeight = 'bold';
          }

          selectedItems.push(item);
        } else {
          if (label) {
            label.style.fontWeight = '';
          }

          unselectedItems.push(item);
        }
      }
    });

    if (selectedItems.length > 0) {
      try {
        const allItems = Array.from(filterList.children);

        filterList.innerHTML = '';

        selectedItems.forEach(item => {
          filterList.appendChild(item);
        });

        unselectedItems.forEach(item => {
          filterList.appendChild(item);
        });

        if (filterList.children.length !== allItems.length) {
          console.warn(`    ⚠️ ITEM COUNT MISMATCH! Before: ${allItems.length}, After: ${filterList.children.length}`);
        }
      } catch (error) {
        console.error(`    ❌ Error during reordering for list ${listIndex + 1}:`, error);
      }
    }
  });

  setTimeout(() => {
    highlightExistingProducts();
  }, 50);
}

function setupFilterTreeObserver() {
  if (window.filterTreeObserver) {
    window.filterTreeObserver.disconnect();
  }

  let filterStabilizationTimeout;

  window.filterTreeObserver = new MutationObserver(mutations => {
    let shouldScheduleReorder = false;

    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList && node.classList.contains('boost-sd__filter-tree-vertical')) {
              shouldScheduleReorder = true;
            } else if (node.querySelector && node.querySelector('.boost-sd__filter-tree-vertical')) {
              shouldScheduleReorder = true;
            }

            if (node.classList && node.classList.contains('boost-sd__filter-option-listitem')) {
              shouldScheduleReorder = true;
            } else if (node.querySelector && node.querySelector('.boost-sd__filter-option-listitem')) {
              shouldScheduleReorder = true;
            }

            if (node.classList && node.classList.contains('boost-sd__filter-option-item')) {
              shouldScheduleReorder = true;
            }
          }
        });
      }
    });

    if (shouldScheduleReorder) {
      clearTimeout(filterStabilizationTimeout);

      filterStabilizationTimeout = setTimeout(() => {
        performStabilizedFilterReorder();
      }, 800);
    }
  });

  window.filterTreeObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function performStabilizedFilterReorder() {
  const filterLists = document.querySelectorAll('.boost-sd__filter-option-listitem');

  if (filterLists.length === 0) {
    console.warn('⚠️ No filter lists found in stabilized check');

    return;
  }

  let totalItemsFound = 0;
  let totalSelectedItems = 0;

  filterLists.forEach(filterList => {
    const itemCount = filterList.children.length;
    totalItemsFound += itemCount;

    if (itemCount > 0) {
      const selectedItems = filterList.querySelectorAll('.boost-sd__filter-option-item-button--selected');
      totalSelectedItems += selectedItems.length;
    }
  });

  if (totalItemsFound > 0 && totalSelectedItems > 0) {
    reorderSelectedFilters();

    setTimeout(() => {
      highlightExistingProducts();
    }, 100);
  }
}

function observeProductListChanges() {
  const productListContainer = document.querySelector('.boost-sd__product-list');

  if (!productListContainer) {
    return;
  }

  if (window.productListObserver) {
    window.productListObserver.disconnect();
  }

  window.productListObserver = new MutationObserver(mutations => {
    let shouldReHighlight = false;

    mutations.forEach(mutation => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        const hasProductCards = Array.from(mutation.addedNodes).some(node => {
          const isProductCard =
            node.nodeType === 1 &&
            ((node.matches && node.matches('product-card')) ||
              (node.querySelector && node.querySelector('product-card')));

          return isProductCard;
        });

        if (hasProductCards) {
          shouldReHighlight = true;
        }
      }
    });

    if (shouldReHighlight) {
      setTimeout(() => {
        highlightExistingProducts();
      }, 200);
    }
  });

  window.productListObserver.observe(productListContainer, {
    childList: true,
    subtree: true,
  });
}

function setupMetafieldFilterEventListeners() {
  // Find all metafield filter buttons
  const metafieldFilterButtons = document.querySelectorAll('[data-metafield-filter]');

  const desktopButtons = [];
  const mobileButtons = [];

  metafieldFilterButtons.forEach(button => {
    const isInMobileContainer = button.closest('#boost-sd_filter-tree-mobile');

    if (isInMobileContainer) {
      mobileButtons.push(button);
    } else {
      desktopButtons.push(button);
    }

    // Remove existing listeners to avoid duplicates
    button.removeEventListener('click', handleMetafieldFilterClick);

    // Add click listener
    button.addEventListener('click', handleMetafieldFilterClick);
  });

  // Set up toggle functionality for filter headers
  const filterHeaders = document.querySelectorAll('.boost-sd__filter-option-title');

  filterHeaders.forEach(header => {
    // Check if this is a mobile filter header
    const isMobileFilter = header.hasAttribute('data-mobile-filter');

    if (isMobileFilter) {
      // For mobile filters, allow normal toggle behavior like desktop
      header.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();

        // Get the target element and the label container
        const toggleTargetId = header.id.replace('-toggle', '-toggle-target');
        const toggleTarget = document.getElementById(toggleTargetId);
        const labelContainerId = header.id.replace('-toggle', '-toggle-target-label');
        const labelContainer = document.getElementById(labelContainerId);

        if (toggleTarget && labelContainer) {
          const isCurrentlyHidden = toggleTarget.classList.contains('boost-sd__filter-option-itemwrapper--hidden');

          // Toggle the state
          if (isCurrentlyHidden) {
            // Show the filter options (open state)
            toggleTarget.classList.remove('boost-sd__filter-option-itemwrapper--hidden');
            toggleTarget.style.display = 'block';
            header.setAttribute('aria-expanded', 'true');
            // Add the --hidden class to label container to indicate it's open
            labelContainer.classList.add('boost-sd__filter-option-label--hidden');
          } else {
            // Hide the filter options (closed state)
            toggleTarget.classList.add('boost-sd__filter-option-itemwrapper--hidden');
            toggleTarget.style.display = 'none';
            header.setAttribute('aria-expanded', 'false');
            // Remove the --hidden class from label container to indicate it's closed
            labelContainer.classList.remove('boost-sd__filter-option-label--hidden');
          }
        }
      });
    } else {
      // For desktop filters, use normal toggle behavior
      header.addEventListener('click', toggleFilterOption);
    }
  });

  // Initialize filters from URL parameters
  initializeFiltersFromUrl();

  // Hide empty filter options after all filters are set up
  hideEmptyFilterOptions();

  // Ensure all mobile filters are visible after initialization
  const mobileFilterTargets = document.querySelectorAll('#boost-sd_filter-tree-mobile [id$="-toggle-target"]');

  mobileFilterTargets.forEach(target => {
    if (target.style.display === 'none' || target.classList.contains('boost-sd__filter-option-itemwrapper--hidden')) {
      // Start with the default hidden state but visible on mobile (as per good structure)
      target.style.display = 'block';
      target.classList.remove('boost-sd__filter-option-itemwrapper--full-height');
      target.classList.add('boost-sd__filter-option-itemwrapper--hidden');
    }
  });
}

function handleMetafieldFilterClick(event) {
  event.preventDefault();
  event.stopPropagation();

  const button = event.currentTarget;
  const filterType = button.dataset.metafieldFilter;
  const value = button.dataset.metafieldValue;
  const isCurrentlyChecked = button.getAttribute('aria-checked') === 'true';
  const newCheckedState = !isCurrentlyChecked;

  // Update the button state
  button.setAttribute('aria-checked', newCheckedState);

  // Apply visual state changes
  if (newCheckedState) {
    button.classList.add('boost-sd__filter-option-item-button--selected');
  } else {
    button.classList.remove('boost-sd__filter-option-item-button--selected');
  }

  // Handle the filter logic
  handleMetafieldFilter(filterType, value, newCheckedState);

  // Update the "SELECTED FILTERS" section
  updateSelectedFiltersSection();
}

function updateSelectedFiltersSection() {
  const refineByContainer = document.querySelector('#bc-refine-by');
  const mobileFilterContainer = document.querySelector('#boost-sd_filter-tree-mobile');
  let mobileRefineByContainer = null;

  if (mobileFilterContainer) {
    mobileRefineByContainer = mobileFilterContainer.querySelector('#bc-refine-by');
  }

  if (!refineByContainer && !mobileRefineByContainer) {
    return;
  }

  // Get all active filters
  const activeFilters = [];

  if (window.activeMetafieldFilters) {
    Object.entries(window.activeMetafieldFilters).forEach(([filterType, selectedValues]) => {
      selectedValues.forEach(value => {
        activeFilters.push({
          type: filterType,
          value: value,
          displayType:
            filterType === 'catalog'
              ? 'CATALOG'
              : filterType === 'course_area'
                ? 'COURSE AREA'
                : filterType === 'series'
                  ? 'SERIES'
                  : filterType.toUpperCase(),
        });
      });
    });
  }

  // If no active filters, clear both sections
  if (activeFilters.length === 0) {
    if (refineByContainer) {
      refineByContainer.innerHTML = '';
    }

    if (mobileRefineByContainer) {
      mobileRefineByContainer.innerHTML = '';
    }

    return;
  }

  // Build the selected filters HTML
  const selectedFiltersHtml = activeFilters
    .map(filter => {
      const filterId = `${filter.value.toLowerCase().replace(/[^a-z0-9]/g, '-')}-clear-filter`;

      return `
      <button aria-label="Clear ${filter.value}"
              class="boost-sd__refine-by-vertical-refine-by-item"
              id="${filterId}"
              data-clear-filter-type="${filter.type}"
              data-clear-filter-value="${filter.value}">
        <div class="boost-sd__refine-by-vertical-refine-by-type">
          <span class="boost-sd__refine-by-vertical-refine-by-option" style="display: none;">
            ${filter.displayType}:
          </span>
          <span class="boost-sd__refine-by-vertical-refine-by-value">
            ${filter.value}
          </span>
        </div>
        <svg class="boost-sd__refine-by-vertical-refine-by-clear"
             xmlns="http://www.w3.org/2000/svg"
             width="12" height="12"
             viewBox="0 0 12 12"
             role="img"
             aria-label="Clear ${filter.value}"
             fill="#222">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M7.41401 6.00001L11.707 1.70701C12.098 1.31601 12.098 0.684006 11.707 0.293006C11.316 -0.0979941 10.684 -0.0979941 10.293 0.293006L6.00001 4.58601L1.70701 0.293006C1.31601 -0.0979941 0.684006 -0.0979941 0.293006 0.293006C-0.0979941 0.684006 -0.0979941 1.31601 0.293006 1.70701L4.58601 6.00001L0.293006 10.293C-0.0979941 10.684 -0.0979941 11.316 0.293006 11.707C0.488006 11.902 0.744006 12 1.00001 12C1.25601 12 1.51201 11.902 1.70701 11.707L6.00001 7.41401L10.293 11.707C10.488 11.902 10.744 12 11 12C11.256 12 11.512 11.902 11.707 11.707C12.098 11.316 12.098 10.684 11.707 10.293L7.41401 6.00001Z" fill="#3D4246"></path>
        </svg>
      </button>`;
    })
    .join('');

  const refineByHtml = `
    <div class="boost-sd__refine-by-vertical-refine-by">
      <div class="boost-sd__refine-by-vertical-refine-by-title">
        <div class="boost-sd__refine-by-vertical-refine-by-heading">SELECTED FILTERS</div>
        <button type="button"
                aria-label="Clear All"
                id="boost-sd__button--clear-all-filters"
                class="boost-sd__button boost-sd__button--clear">
          <span class="boost-sd__button-text">Clear All</span>
        </button>
      </div>
      <div class="boost-sd__refine-by-vertical-refine-by-list">
        ${selectedFiltersHtml}
      </div>
    </div>`;

  if (refineByContainer) {
    refineByContainer.innerHTML = refineByHtml;
  }

  if (mobileRefineByContainer) {
    mobileRefineByContainer.innerHTML = refineByHtml;
  }

  // Add event listeners for the clear buttons
  setupSelectedFiltersClearListeners();
}

function setupSelectedFiltersClearListeners() {
  // Clear individual filter
  const clearFilterButtons = document.querySelectorAll('[data-clear-filter-type]');

  clearFilterButtons.forEach(button => {
    button.addEventListener('click', function () {
      const filterType = this.dataset.clearFilterType;
      const filterValue = this.dataset.clearFilterValue;

      // Find the corresponding filter button and uncheck it
      const filterButton = document.querySelector(
        `[data-metafield-filter="${filterType}"][data-metafield-value="${filterValue}"]`
      );

      if (filterButton) {
        filterButton.setAttribute('aria-checked', 'false');
        filterButton.classList.remove('boost-sd__filter-option-item-button--selected');

        // Update the filter logic
        handleMetafieldFilter(filterType, filterValue, false);
        updateSelectedFiltersSection();
      }
    });
  });

  // Clear all filters - handle both desktop and mobile buttons
  const clearAllButton = document.querySelector('#boost-sd__button--clear-all-filters');
  const mobileClearAllButton = document.querySelector('#boost-sd__button-toolbar-mobile-clear');

  function clearAllFilters() {
    // Clear all active filters
    if (window.activeMetafieldFilters) {
      Object.keys(window.activeMetafieldFilters).forEach(filterType => {
        window.activeMetafieldFilters[filterType].clear();
      });
    }

    // Uncheck all filter buttons
    const allFilterButtons = document.querySelectorAll('[data-metafield-filter]');

    allFilterButtons.forEach(button => {
      button.setAttribute('aria-checked', 'false');
      button.classList.remove('boost-sd__filter-option-item-button--selected');
    });

    // Re-render all products and update UI
    applyMetafieldFilters();
    updateSelectedFiltersSection();

    // Update browser URL to remove all filters
    updateBrowserUrl();
  }

  if (clearAllButton) {
    clearAllButton.addEventListener('click', clearAllFilters);
  }

  if (mobileClearAllButton) {
    mobileClearAllButton.addEventListener('click', clearAllFilters);
  }
}

function toggleFilterOption(event) {
  const header = event.currentTarget;
  const isExpanded = header.getAttribute('aria-expanded') === 'true';
  const newExpandedState = !isExpanded;

  header.setAttribute('aria-expanded', newExpandedState);

  const filterBody = header.parentElement.nextElementSibling;

  if (filterBody) {
    filterBody.style.display = newExpandedState ? 'block' : 'none';
  }
}

function extractAndRenderMetafieldFilters(products) {
  const metafieldFilters = {
    catalog: new Set(),
    course_area: new Set(),
    series: new Set(),
  };

  products.forEach(product => {
    if (product.metafields) {
      product.metafields.forEach(metafield => {
        if (metafield.namespace === 'asp') {
          switch (metafield.key) {
            case 'catalog':
              if (metafield.value) {
                try {
                  const catalogValues = JSON.parse(metafield.value);

                  catalogValues.forEach(value => metafieldFilters.catalog.add(value));
                } catch {
                  metafieldFilters.catalog.add(metafield.value);
                }
              }

              break;

            case 'course_area':
              if (metafield.value) {
                try {
                  const courseAreaValues = JSON.parse(metafield.value);

                  courseAreaValues.forEach(value => metafieldFilters.course_area.add(value));
                } catch {
                  metafieldFilters.course_area.add(metafield.value);
                }
              }

              break;

            case 'series':
              if (metafield.value) {
                metafieldFilters.series.add(metafield.value);
              }

              break;
          }
        }
      });
    }
  });

  renderMetafieldFiltersWithBoostStructure(metafieldFilters);
}

function updateSearchFormTitle() {
  const titleElement = document.querySelector('.boost-sd__search-form-title');

  if (!titleElement) {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const searchQuery = urlParams.get('q');

  if (searchQuery && searchQuery.trim()) {
    titleElement.textContent = `Search results for "${searchQuery.trim()}"`;
  } else {
    titleElement.textContent = 'General Title';
  }
}

function updateSearchResults(data) {
  if (!data) {
    return;
  }

  window.lastSearchData = data;

  // Update the search form title based on current URL params
  updateSearchFormTitle();

  if (data.meta?.money_format) {
    window.boostApiMoneyFormat = data.meta.money_format;
  }

  try {
    if (data.products) {
      renderProducts(data.products);

      extractAndRenderMetafieldFilters(data.products);
    }

    if (data.filter) {
      renderFilters(data.filter);
    }

    if (data.total_product !== undefined) {
      const productCountElement = document.querySelector('.boost-sd__product-count');

      const toolbarProductCountElement = document.querySelector(
        '.boost-sd__toolbar-item--product-count .result-counting'
      );

      if (productCountElement) {
        const count = data.total_product;
        const productsPerPage = 16;
        const startEntry = count > 0 ? 1 : 0;
        const endEntry = Math.min(productsPerPage, count);

        productCountElement.innerHTML = `
          Showing <span>${startEntry}</span> to <span>${endEntry}</span> of <span>${count}</span> entries
        `;
      }

      if (toolbarProductCountElement) {
        const count = data.total_product;
        const productsPerPage = 16;
        const startEntry = count > 0 ? 1 : 0;
        const endEntry = Math.min(productsPerPage, count);

        toolbarProductCountElement.innerHTML = `
          Showing <span>${startEntry}</span> to <span>${endEntry}</span> of <span>${count}</span> entries
        `;
      }

      const showResultButton = document.querySelector(
        '.boost-sd__filter-tree-vertical-show-result .boost-sd__button--result'
      );

      if (showResultButton) {
        const buttonText = showResultButton.querySelector('.boost-sd__button-text');

        if (buttonText) {
          buttonText.textContent = `Show result (${data.total_product})`;
        }
      }

      updatePagination(data.total_product);
    }
  } catch (error) {
    console.error('❌ DEBUG: Error in updateSearchResults:', error);
    console.error('Error updating search results:', error);
  }
}

function showLoadingState() {
  const productList = document.querySelector('.boost-sd__product-list');
  const filterTree = document.querySelector('.boost-sd__filter-tree-vertical');

  if (productList) {
    productList.style.opacity = '0.5';
    productList.style.pointerEvents = 'none';
  }

  if (filterTree) {
    filterTree.style.opacity = '0.5';
  }
}

function hideLoadingState() {
  const productList = document.querySelector('.boost-sd__product-list');
  const filterTree = document.querySelector('.boost-sd__filter-tree-vertical');

  if (productList) {
    productList.style.opacity = '1';
    productList.style.pointerEvents = 'auto';
  }

  if (filterTree) {
    filterTree.style.opacity = '1';
  }
}

function updateURLParameter(query) {
  const url = new URL(window.location);

  if (query && query.trim()) {
    url.searchParams.set('q', query.trim());
  } else {
    url.searchParams.delete('q');
  }

  window.history.pushState({ search: query }, '', url.toString());
}

function handleInstantSearch(query) {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(async () => {
    try {
      updateURLParameter(query);
      showLoadingState();

      if (query && query.trim()) {
        const searchData = await fetchInstantSearchResults(query.trim());

        updateSearchResults(searchData);
      } else {
        const searchData = await fetchInstantSearchResults('*');

        updateSearchResults(searchData);
      }
    } catch (error) {
      console.error('❌ DEBUG: Instant search failed:', error);
      console.error('Instant search failed:', error);
    } finally {
      hideLoadingState();
    }
  }, 300);
}

function setupInstantSearch(input) {
  if (input.dataset.eventBound === 'true') {
    return;
  }

  // Mark input as having events bound
  input.dataset.eventBound = 'true';

  let lastValue = input.value;
  let fetchInterceptionActive = false;

  // Activate fetch interception only when user starts typing
  function activateFetchInterception() {
    if (!fetchInterceptionActive) {
      interceptFetch();
      fetchInterceptionActive = true;
    }
  }

  // Handle Enter key
  input.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') {
      e.preventDefault();

      const query = this.value.trim();

      if (query !== lastValue) {
        lastValue = query;
        activateFetchInterception();
        handleInstantSearch(query);
      }
    }
  });

  // Handle input changes with debounce
  input.addEventListener('input', function () {
    const query = this.value.trim();

    if (query !== lastValue) {
      lastValue = query;
      activateFetchInterception();
      handleInstantSearch(query);
    }
  });

  // Prevent form submission to avoid page reload
  const form = input.closest('form');

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();

      const query = input.value.trim();
      activateFetchInterception();
      handleInstantSearch(query);
    });
  }
}

function modifySearchInputPlaceholder(input) {
  if (document.body.classList.contains('templateIndex')) {
    const targetPlaceholder = 'Search by title, author & topic';
    input.placeholder = targetPlaceholder;

    // Add persistent monitoring to prevent Boost from overriding our change
    if (!input.dataset.placeholderMonitor) {
      input.dataset.placeholderMonitor = 'true';

      // Use MutationObserver to watch for attribute changes
      const placeholderObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'placeholder') {
            if (input.placeholder !== targetPlaceholder) {
              input.placeholder = targetPlaceholder;
            }
          }
        });
      });

      placeholderObserver.observe(input, {
        attributes: true,
        attributeFilter: ['placeholder'],
      });

      let checkCount = 0;
      const maxChecks = 100;

      const intervalId = setInterval(() => {
        checkCount++;

        if (document.body.classList.contains('templateIndex') && input.placeholder !== targetPlaceholder) {
          input.placeholder = targetPlaceholder;
        }

        // Stop monitoring if input is removed from DOM or after max checks
        if (!document.body.contains(input) || checkCount >= maxChecks) {
          clearInterval(intervalId);
          placeholderObserver.disconnect();
        }
      }, 100);
    }

    // Also force update using setAttribute as backup
    input.setAttribute('placeholder', targetPlaceholder);
  }
}

function checkAndModifyAllSearchInputs() {
  const allSearchInputs = document.querySelectorAll(
    'input[name="q"], .boost-sd__search-widget-init-input, .boost-sd__search-form-input'
  );

  allSearchInputs.forEach(input => {
    modifySearchInputPlaceholder(input);
  });
}

function startGlobalPlaceholderMonitoring() {
  if (!document.body.classList.contains('templateIndex')) {
    return;
  }

  // Global interval that continuously monitors all search inputs
  const globalMonitoringInterval = setInterval(() => {
    if (!document.body.classList.contains('templateIndex')) {
      clearInterval(globalMonitoringInterval);

      return;
    }

    const allSearchInputs = document.querySelectorAll(
      'input[name="q"], .boost-sd__search-widget-init-input, .boost-sd__search-form-input'
    );

    const targetPlaceholder = 'Search by title, author & topic';

    allSearchInputs.forEach(input => {
      if (input.placeholder !== targetPlaceholder) {
        input.placeholder = targetPlaceholder;
        input.setAttribute('placeholder', targetPlaceholder);
      }
    });
  }, 250);

  // Also add a mutation observer to catch new inputs being added
  const globalObserver = new MutationObserver(mutations => {
    if (!document.body.classList.contains('templateIndex')) {
      return;
    }

    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            // Check if new search inputs were added
            const newSearchInputs = node.querySelectorAll
              ? node.querySelectorAll(
                  'input[name="q"], .boost-sd__search-widget-init-input, .boost-sd__search-form-input'
                )
              : [];

            if (newSearchInputs.length > 0) {
              const targetPlaceholder = 'Search by title, author & topic';

              newSearchInputs.forEach(input => {
                input.placeholder = targetPlaceholder;
                input.setAttribute('placeholder', targetPlaceholder);
              });
            }

            // Check if the node itself is a search input
            if (
              node.tagName === 'INPUT' &&
              (node.name === 'q' || node.classList.contains('boost-sd__search-widget-init-input'))
            ) {
              const targetPlaceholder = 'Search by title, author & topic';
              node.placeholder = targetPlaceholder;
              node.setAttribute('placeholder', targetPlaceholder);
            }
          }
        });
      }
    });
  });

  globalObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

function isSearchPage() {
  const urlParams = new URLSearchParams(window.location.search);
  const hasSearchQuery = urlParams.has('q');

  const isSearchPath =
    window.location.pathname.includes('/search') || window.location.pathname.includes('/pages/search');

  const hasBoostSearchContainer = document.querySelector(
    '.boost-sd__search-form, .boost-sd__product-list, .boost-sd__filter-wrapper'
  );

  // Check for the specific input element you mentioned
  const hasBoostSearchInput = document.querySelector('input[name="q"].boost-sd__search-widget-init-input');

  const result = hasSearchQuery || isSearchPath || hasBoostSearchContainer || hasBoostSearchInput;

  return result;
}

function findSearchInput(callback) {
  if (!isSearchPage()) {
    return;
  }

  let attempts = 0;
  const maxAttempts = 50;

  function checkForInput() {
    attempts++;

    // Try multiple selectors for Boost search inputs
    const selectors = [
      '.boost-sd__search-form-input',
      'input[name="q"].boost-sd__search-widget-init-input',
      'input[name="q"]',
    ];

    let foundInput = null;

    for (const selector of selectors) {
      const element = document.querySelector(selector);

      if (element) {
        foundInput = element;

        break;
      }
    }

    // Check if Boost is ready
    const isBoostReady = window.boostWidgetIntegration?.status === 'ready';

    if (foundInput && isBoostReady) {
      modifySearchInputPlaceholder(foundInput);
      callback(foundInput);

      return;
    }

    // If we found input but Boost isn't ready yet, still try to modify placeholder
    if (foundInput && !isBoostReady) {
      modifySearchInputPlaceholder(foundInput);
    }

    if (attempts >= maxAttempts) {
      console.warn('🔍 DEBUG: Max attempts reached, giving up');

      return;
    }

    setTimeout(checkForInput, 200);
  }

  checkForInput();
}

function highlightExistingProducts() {
  const currentSearchQuery = getCurrentSearchQuery();

  if (!currentSearchQuery) {
    return;
  }

  // Add CSS styles for highlighting
  addSearchHighlightStyles();

  // Function to attempt highlighting with retry logic
  function attemptHighlighting(retryCount = 0) {
    const maxRetries = 10;
    const retryDelay = 500;

    // Find existing product titles and highlight them
    const productTitles = document.querySelectorAll('.ais-hit--title a, .ais-hit--title');
    const productSubtitles = document.querySelectorAll('.ais-hit--subtitle');

    if (productTitles.length > 0 || productSubtitles.length > 0) {
      productTitles.forEach(titleElement => {
        const originalText = titleElement.textContent || titleElement.innerText;

        const highlightedText = highlightSearchKeywords(originalText, currentSearchQuery);

        if (highlightedText !== originalText) {
          titleElement.innerHTML = highlightedText;
        }
      });

      productSubtitles.forEach(subtitleElement => {
        const originalText = subtitleElement.textContent || subtitleElement.innerText;

        const highlightedText = highlightSearchKeywords(originalText, currentSearchQuery);

        if (highlightedText !== originalText) {
          subtitleElement.innerHTML = highlightedText;
        }
      });
    } else if (retryCount < maxRetries) {
      setTimeout(() => attemptHighlighting(retryCount + 1), retryDelay);
    }
  }

  // Start the highlighting attempt
  attemptHighlighting();
}

function initializeInstantSearch() {
  updateSearchFormTitle();

  highlightExistingProducts();

  setupSortingEventListeners();

  observeProductListChanges();

  setupFilterReorderingListeners();
  setupFilterTreeObserver();

  setTimeout(() => {
    performInitialFilterReorderCheck();
  }, 1500);

  findSearchInput(input => {
    setupInstantSearch(input);
  });

  // Also try to modify all search inputs immediately and after delays
  checkAndModifyAllSearchInputs();

  setTimeout(() => {
    checkAndModifyAllSearchInputs();
  }, 1000);

  setTimeout(() => {
    checkAndModifyAllSearchInputs();
  }, 3000);

  setTimeout(() => {
    checkAndModifyAllSearchInputs();
  }, 5000);

  startGlobalPlaceholderMonitoring();
}

function performInitialFilterReorderCheck() {
  const filterLists = document.querySelectorAll('.boost-sd__filter-option-listitem');

  if (filterLists.length === 0) {
    return;
  }

  let hasAnySelectedFilters = false;

  filterLists.forEach(filterList => {
    const itemCount = filterList.children.length;

    if (itemCount > 0) {
      const selectedItems = filterList.querySelectorAll('.boost-sd__filter-option-item-button--selected');

      if (selectedItems.length > 0) {
        hasAnySelectedFilters = true;
      }
    }
  });

  if (hasAnySelectedFilters) {
    reorderSelectedFilters();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeInstantSearch);
} else {
  initializeInstantSearch();
}

// Also listen for dynamic content changes
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            // Check for search inputs in newly added nodes
            const searchInputs = node.querySelectorAll ? node.querySelectorAll('input[name="q"]') : [];

            if (searchInputs.length > 0) {
              searchInputs.forEach(input => {
                modifySearchInputPlaceholder(input);

                if (!input.dataset.eventBound) {
                  setupInstantSearch(input);
                }
              });
            }

            // Check if the node itself is a search input
            if (node.tagName === 'INPUT' && node.name === 'q') {
              modifySearchInputPlaceholder(node);

              if (!node.dataset.eventBound) {
                setupInstantSearch(node);
              }
            }

            // Also check for any Boost search widgets that might have been added
            const boostSearchWidgets = node.querySelectorAll
              ? node.querySelectorAll('.boost-sd__search-widget, .boost-sd__search-form')
              : [];

            if (boostSearchWidgets.length > 0) {
              setTimeout(() => {
                const allSearchInputs = document.querySelectorAll('input[name="q"]');

                allSearchInputs.forEach(input => {
                  if (document.body.classList.contains('templateIndex')) {
                    modifySearchInputPlaceholder(input);
                  }
                });
              }, 100);
            }
          }
        });
      }

      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            // Element node
            // Check if this node or its children contain filter items
            const filterItems = node.querySelectorAll ? node.querySelectorAll('.boost-sd__filter-option-item') : [];

            if (filterItems.length > 0) {
              setTimeout(() => {
                hideEmptyFilterOptions();
              }, 100);
            }

            // Also check for filter containers that might get populated later
            if (
              node.querySelector &&
              (node.querySelector('.boost-sd__filter-option-listitem') ||
                node.classList?.contains('boost-sd__filter-option-listitem'))
            ) {
              setTimeout(() => {
                hideEmptyFilterOptions();
              }, 300);

              setTimeout(() => {
                hideEmptyFilterOptions();
              }, 600);

              setTimeout(() => {
                hideEmptyFilterOptions();
              }, 1000);
            }

            // Check if we're adding LI elements directly
            if (node.tagName === 'LI' && node.classList?.contains('boost-sd__filter-option-item')) {
              setTimeout(() => {
                hideEmptyFilterOptions();
              }, 50);
            }

            // Check for search inputs
            const searchInputs = node.querySelectorAll ? node.querySelectorAll('input[name="q"]') : [];

            if (searchInputs.length > 0) {
              searchInputs.forEach(input => {
                if (!input.dataset.eventBound) {
                  setupInstantSearch(input);
                }
              });
            }

            // Check for sorting options
            const sortingOptions = node.querySelectorAll ? node.querySelectorAll('.boost-sd__sorting-option-item') : [];

            if (sortingOptions.length > 0) {
              setTimeout(() => {
                setupSortingEventListeners();
              }, 100);
            }

            // Check for filter tree changes
            const filterTrees = node.querySelectorAll ? node.querySelectorAll('.boost-sd__filter-tree-vertical') : [];

            if (filterTrees.length > 0) {
              setTimeout(() => {
                reorderSelectedFilters();
              }, 200);
            }

            // Check if this is a filter tree itself
            if (node.classList?.contains('boost-sd__filter-tree-vertical')) {
              setTimeout(() => {
                reorderSelectedFilters();
              }, 200);
            }
          }
        });
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}
